import { gql } from '@apollo/client';

// Account-related queries
export const GET_USER_ACCOUNTS = gql`
  query GetUserAccounts {
    userAccounts {
      id
      name
      createdAt
      plan
      isActive
      techContactEmail
      urlShortening
      bitlyToken
      firstCommentPrefix
      utmCampaign
      timezone
      products {
        product
        status
        plan
      }
    }
  }
`;

export const GET_USER_PROFILE = gql`
  query GetUserProfile {
    me {
      id
      email
    }
  }
`;

// The Reach topic picker (TopicsSection), a page at a time: media type and
// search run on the server, newest first (topicPicker).
export const GET_TOPIC_PICKER = gql`
  query GetTopicPicker(
    $accountId: ID!
    $mediaTypes: [String!]
    $search: String
    $offset: Int
    $limit: Int
  ) {
    topicPicker(
      accountId: $accountId
      mediaTypes: $mediaTypes
      search: $search
      offset: $offset
      limit: $limit
    ) {
      total
      offset
      limit
      items {
        id
        title
        summary
        mediaType
        mediaUrl
        publishedDate
        publishStatus
        destinationUrl
        alreadyPosted
        category
        keywords
        contentType
        atomizedAt
        createdAt
        updatedAt
      }
    }
  }
`;

// The Content list, a page at a time: filter, categories and search run on the
// server, with whole-account counts and category chips (topicsPage).
export const GET_TOPICS_PAGE = gql`
  query GetTopicsPage(
    $accountId: ID!
    $filter: String
    $categories: [String!]
    $search: String
    $atomize: Boolean
    $offset: Int
    $limit: Int
  ) {
    topicsPage(
      accountId: $accountId
      filter: $filter
      categories: $categories
      search: $search
      atomize: $atomize
      offset: $offset
      limit: $limit
    ) {
      total
      offset
      limit
      counts {
        all
        review
        draft
        scheduled
        live
      }
      categories {
        name
        count
      }
      items {
        id
        title
        summary
        mediaType
        mediaUrl
        publishedDate
        publishStatus
        destinationUrl
        alreadyPosted
        category
        keywords
        contentType
        atomizedAt
        createdAt
        updatedAt
        verifiedFacts
        dataSource {
          id
          name
          type
        }
      }
    }
  }
`;

// Full content item (article/video/image/webpage) for the review view.
export const GET_CONTENT_ITEM = gql`
  query GetContentItem($id: ID!) {
    topic(id: $id) {
      id
      accountId
      title
      summary
      mediaType
      # mediaUrl is the identity column (pinned to '' for cards and WP posts).
      # primaryMediaUrl is the display pointer maintained by the media layer.
      mediaUrl
      primaryMediaUrl
      publishedDate
      publishStatus
      destinationUrl
      category
      keywords
      bodyText
      contentType
      contentMediaType
      mediaItems
      taxonomies
      customFields
      verifiedFacts
      keyPeople
      keyOrganizations
      keyLocations
      atomizedAt
      dataSource {
        id
        name
        type
      }
    }
  }
`;

export const GET_TOPIC_VERSIONS = gql`
  query GetTopicVersions($topicId: ID!) {
    topicVersions(topicId: $topicId) {
      id
      topicId
      versionNumber
      changeType
      snapshot
      createdAt
      createdBy
      createdByEmail
    }
  }
`;

export const GET_POSTS = gql`
  query GetSocialPosts($socialProfileIds: [ID!]!) {
    socialPosts(socialProfileIds: $socialProfileIds) {
      id
      title
      summary
      postUrl
      imageUrl
      thoughts
      scheduleTime
      socialPublishDate
      platformPostId
      platform
      socialProfileId
      deletionStatus
      postType
      socialProfile {
        profileName
        profileUsername
      }
      createdBy {
        email
      }
      notes
      isDraft
    }
  }
`;
export const GET_ACCOUNT_DETAILS = gql`
  query GetAccountDetails($id: ID!) {
    account(id: $id) {
      id
      name
      plan
      techContactEmail
      urlShortening
      bitlyToken
      firstCommentPrefix
      utmCampaign
      timezone
      users {
        id
        user {
          id
          email
          lastSeenAt
        }
        role
        createdAt
      }
      socialProfiles {
        id
        platform
        profileName
        profileUsername
        profileLink
        isActive
        isPublishing
        createdAt
      }
      dataSources {
        id
        name
        type
        url
        isActive
      }
      activeOrder {
        id
        plan
        createdAt
        updatedAt
        monthlyRate
        billingInterval
        orderEffectiveStartDate
        orderEffectiveEndDate
        orderNextBillDate
        orderCancelDate
      }
      billing {
        status
        periodEnd
        trialEnd
        cancelAtPeriodEnd
      }
      pausedMembers {
        id
        email
        role
        pausedAt
      }
    }
  }
`;
export const GET_ACCOUNT_BILLING_DETAILS = gql`
  query GetAccountBillingDetails($accountId: ID!) {
    billingDetails(accountId: $accountId) {
      id
      cardLast4
      cardExpMonth
      cardExpYear
      isDefault
      isRemovable
    }
  }
`;
// Social profiles queries
export const GET_SOCIAL_PROFILES = gql`
  query GetSocialProfiles($accountId: ID!) {
    socialProfiles(accountId: $accountId) {
      id
      platform
      createdAt
      profileId
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
    }
  }
`;

// Data sources queries
export const GET_DATA_SOURCES = gql`
  query GetDataSources($accountId: ID!) {
    dataSources(accountId: $accountId) {
      id
      name
      type
      url
      createdAt
      isActive
    }
  }
`;

// Claude Cowork / remote MCP connector (metadata only — no plaintext secrets).
export const GET_COWORK_MCP_CONNECTOR = gql`
  query GetCoworkMcpConnector($accountId: ID!) {
    coworkMcpConnector(accountId: $accountId) {
      accountId
      dataSourceId
      name
      isActive
      mcpUrl
      tokenId
      tokenPrefix
      label
      createdAt
      lastUsedAt
      hasCredentials
    }
  }
`;

// Atomization config queries
export const GET_DATA_SOURCE_CUSTOM_FIELDS = gql`
  query GetDataSourceCustomFields($dataSourceId: ID!) {
    dataSourceCustomFields(dataSourceId: $dataSourceId) {
      id
      dataSourceId
      fieldName
      sourceKind
      locator
      isActive
      taxonomyKey
      valueDelimiter
    }
  }
`;

export const GET_ACCOUNT_TAXONOMY = gql`
  query GetAccountTaxonomy($accountId: ID!) {
    accountTaxonomy(accountId: $accountId) {
      key
      terms
      neverDerive
      isFreeform
    }
  }
`;

export const GET_ACCOUNT_TOPIC_FIELDS = gql`
  query GetAccountTopicFields($accountId: ID!) {
    accountTopicFields(accountId: $accountId) {
      id
      accountId
      fieldKey
      label
      description
      fieldType
      required
      options
      uiGroup
      sortOrder
      isActive
      isPublic
      aliases
      facetsPublic
    }
  }
`;

export const GET_DATA_SOURCE_TAXONOMY_SUBSET = gql`
  query GetDataSourceTaxonomySubset($dataSourceId: ID!) {
    dataSourceTaxonomySubset(dataSourceId: $dataSourceId) {
      key
      terms
    }
  }
`;

export const GET_AUDIT_LOGS = gql`
  query GetAuditLogs($accountId: ID!) {
    auditLogs(accountId: $accountId) {
      id
      tableName
      action
      payload
      userEmail
      createdAt
    }
  }
`;

// Connections → API keys: the account's live keys (prefix only).
export const GET_API_KEYS = gql`
  query GetApiKeys($accountId: ID!) {
    apiKeys(accountId: $accountId) {
      id
      label
      prefix
      scope
      createdAt
      lastUsedAt
    }
  }
`;

// Every agent that can act on the site (any MCP key): Team and Connections list them.
export const GET_ACCOUNT_AGENTS = gql`
  query GetAccountAgents($accountId: ID!) {
    accountAgents(accountId: $accountId) {
      id
      name
      via
      keyPrefix
      connectedBy
      connectedAt
      lastActiveAt
      active
    }
  }
`;

// Account → Activity log: the server writes each sentence (it can name the object).
export const GET_ACTIVITY_LOG = gql`
  query GetActivityLog($accountId: ID!, $limit: Int) {
    activityLog(accountId: $accountId, limit: $limit) {
      id
      at
      what
      who
      area
    }
  }
`;

// Project queries
export const GET_PROJECTS = gql`
  query GetProjects($accountId: ID!) {
    projects(accountId: $accountId) {
      id
      accountId
      name
      kind
      plan
      deadline
      status
      progress
      campaignStory
      landingPageUrl
      landingPageStatus
      color
      createdAt
      updatedAt
      createdBy
      teamMembers {
        id
        userId
        role
        user {
          id
          email
        }
      }
      posts {
        id
        status
      }
    }
  }
`;

export const GET_PROJECT = gql`
  query GetProject($id: ID!) {
    project(id: $id) {
      id
      accountId
      name
      plan
      deadline
      status
      progress
      campaignStory
      landingPageUrl
      landingPageStatus
      color
      notes
      createdAt
      updatedAt
      createdBy
      teamMembers {
        id
        userId
        role
        user {
          id
          email
        }
      }
    }
  }
`;

export const GET_PROJECT_POSTS = gql`
  query GetProjectPosts($projectId: ID!) {
    projectPosts(projectId: $projectId) {
      id
      projectId
      accountId
      title
      content
      status
      assignedTo
      scheduledDate
      dueDate
      publishedAt
      createdAt
      updatedAt
      createdBy
      postType
      assignedUser {
        id
        email
      }
      targets {
        id
        socialProfileId
        platform
        socialProfile {
          id
          profileName
          platform
        }
      }
      socialPosts {
        id
        title
        platform
        status
      }
      topic {
        id
        title
        summary
        mediaType
        mediaUrl
        destinationUrl
        keywords
      }
      notes
    }
  }
`;

export const GET_PROJECT_POST = gql`
  query GetProjectPost($id: ID!) {
    projectPost(id: $id) {
      id
      projectId
      accountId
      title
      content
      status
      assignedTo
      scheduledDate
      dueDate
      publishedAt
      createdAt
      updatedAt
      createdBy
      postType
      assignedUser {
        id
        email
      }
      targets {
        id
        socialProfileId
        platform
        socialProfile {
          id
          profileName
          platform
        }
      }
      socialPosts {
        id
        title
        platform
        status
      }
      topic {
        id
        title
        summary
        mediaType
        mediaUrl
        destinationUrl
        keywords
      }
      notes
    }
  }
`;

export const ADAPT_CONTENT_FOR_PLATFORMS = gql`
  query AdaptContentForPlatforms($content: String!) {
    adaptContentForPlatforms(content: $content) {
      platforms {
        platform
        content
        characterCount
        isTruncated
      }
    }
  }
`;

export const GENERATE_SOLUTION = gql`
  query GenerateSolution($situation: String!) {
    generateSolution(situation: $situation) {
      solution
      plan
    }
  }
`;

// Research queries
export const GET_RESEARCH_SETTINGS = gql`
  query GetResearchSettings($accountId: ID!) {
    researchSettings(accountId: $accountId) {
      id
      accountId
      searchEndpoint
      searchApiKey
      searchApiVersion
      factsIndex
      photosIndex
      openaiProvider
      openaiEndpoint
      openaiApiKey
      openaiModel
      isActive
      updatedAt
    }
  }
`;

const RESEARCH_HIT_FIELDS = `
  type
  topicId
  postId
  factIndex
  photoIndex
  eventDate
  readableDate
  sortableDate
  factText
  factTextAr
  attribution
  sourceType
  sourceQuote
  articleTitle
  url
  keyPeople
  keyOrganizations
  topics
  where
  tags
  locations
  photoDate
  photoLocation
  caption
  headline
  credit
  keywords
  thumbnailUrl
  citationId
  score
`;

export const RESEARCH_ASK = gql`
  query ResearchAsk($accountId: ID!, $query: String!, $lang: String, $sessionId: String, $sessionSeq: Int) {
    researchAsk(accountId: $accountId, query: $query, lang: $lang, sessionId: $sessionId, sessionSeq: $sessionSeq) {
      answer
      followups
      queryId
      searchMeta {
        dateLabel
        locationTerms
        nameTerms
      }
      citations {
        ${RESEARCH_HIT_FIELDS}
      }
      hits {
        ${RESEARCH_HIT_FIELDS}
      }
    }
  }
`;

export const RESEARCH_SEARCH = gql`
  query ResearchSearch($accountId: ID!, $query: String!, $lang: String) {
    researchSearch(accountId: $accountId, query: $query, lang: $lang) {
      ${RESEARCH_HIT_FIELDS}
    }
  }
`;

export const GET_ASSEMBLER_SOURCES = gql`
  query AssemblerSources(
    $accountId: ID!
    $mode: String
    $keyword: String
    $dateFrom: String
    $dateTo: String
  ) {
    assemblerSources(
      accountId: $accountId
      mode: $mode
      keyword: $keyword
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      id
      title
      date
    }
  }
`;

// ── Media asset management ──────────────────────────────────────────────────
// Renditions come back with the asset so the grid can resolve a thumbnail
// without a second round trip per tile.

/** A video's state (Cloudflare Stream) — on library entries that are videos. */
export const MEDIA_VIDEO_FIELDS = gql`
  fragment MediaVideoFields on MediaVideo {
    status
    durationSeconds
    width
    height
    sizeBytes
    posterUrl
    posterAssetId
    posterTimeSeconds
    frameUrl
    playerUrl
    mp4Url
    mp4Status
    error
  }
`;

/** One video, after asking Stream for its state when it isn't settled. */
export const GET_MEDIA_VIDEO = gql`
  query GetMediaVideo($accountId: ID!, $assetId: ID!) {
    mediaVideo(accountId: $accountId, assetId: $assetId) {
      id
      originalFilename
      video {
        ...MediaVideoFields
      }
    }
  }
  ${MEDIA_VIDEO_FIELDS}
`;

/** What the plan allows for media and what the site has used. */
export const GET_MEDIA_ALLOWANCE = gql`
  query GetMediaAllowance($accountId: ID!) {
    mediaAllowance(accountId: $accountId) {
      planLabel
      storageUsedBytes
      storageLimitBytes
      videoUsedSeconds
      videoLimitSeconds
      viewingMonth
      minutesWatched
      includedViewMinutes
      overageMinutes
      overageCentsSoFar
      overageRate
      overStorage
      overVideo
    }
  }
`;

export const GET_MEDIA_ASSETS = gql`
  query GetMediaAssets($accountId: ID!, $limit: Int, $offset: Int, $linkedTo: ID) {
    mediaAssets(accountId: $accountId, limit: $limit, offset: $offset, linkedTo: $linkedTo) {
      total
      limit
      offset
      assets {
        id
        sha256
        mime
        bytes
        width
        height
        originalFilename
        captureMeta
        createdAt
        usageCount
        usage {
          ownerType
          ownerId
          role
          position
          title
          status
        }
        renditions {
          id
          kind
          url
          width
          height
          isCurrent
          createdAt
          transform
        }
        video {
          ...MediaVideoFields
        }
      }
    }
  }
  ${MEDIA_VIDEO_FIELDS}
`;

export const GET_MEDIA_RENDITIONS = gql`
  query GetMediaRenditions($accountId: ID!, $assetId: ID!) {
    mediaRenditions(accountId: $accountId, assetId: $assetId) {
      id
      kind
      url
      width
      height
      bytes
      isCurrent
      supersededAt
      createdAt
      transform
    }
  }
`;

export const GET_OWNER_MEDIA = gql`
  query GetOwnerMedia($accountId: ID!, $ownerType: String!, $ownerId: ID!) {
    ownerMedia(accountId: $accountId, ownerType: $ownerType, ownerId: $ownerId) {
      assetId
      role
      position
      isPrimary
      altText
      url
      width
      height
      servingKind
    }
  }
`;

/**
 * Collection grid, filtered and paginated server-side.
 *
 * `mediaUrl` here is the resolved DISPLAY url (the service coalesces
 * primary_media_url over the identity column), not topics.media_url.
 */
export const GET_COLLECTION_ITEMS = gql`
  query GetCollectionItems(
    $accountId: ID!
    $q: String
    $status: String
    $sport: String
    $league: String
    $manufacturer: String
    $productSet: String
    $team: String
    $player: String
    $limit: Int
    $offset: Int
  ) {
    collectionItems(
      accountId: $accountId
      q: $q
      status: $status
      sport: $sport
      league: $league
      manufacturer: $manufacturer
      productSet: $productSet
      team: $team
      player: $player
      limit: $limit
      offset: $offset
    ) {
      total
      limit
      offset
      items {
        id
        sku
        title
        mediaUrl
        publishStatus
        customFields
        taxonomies
        listing {
          askingPrice
          currency
          status
          soldPrice
        }
      }
    }
  }
`;

export const GET_COLLECTION_FACETS = gql`
  query GetCollectionFacets($accountId: ID!) {
    collectionFacets(accountId: $accountId) {
      dimension
      terms {
        term
        count
      }
    }
  }
`;

export const GET_COLLECTION_PORTFOLIO = gql`
  query GetCollectionPortfolio($accountId: ID!) {
    collectionPortfolio(accountId: $accountId) {
      cards
      withListing
      listed
      sold
      askingTotal
      soldTotal
      acquiredTotal
      realisedMargin
    }
  }
`;

// One collection item, for the detail screen. Collect's own read rather than
// the Atomizer `topic` query, which a Collect-only account cannot reach.
export const GET_COLLECTION_ITEM = gql`
  query GetCollectionItem($accountId: ID!, $id: ID!) {
    collectionItem(accountId: $accountId, id: $id) {
      id
      sku
      title
      summary
      mediaUrl
      publishStatus
      publishedDate
      customFields
      taxonomies
      updatedAt
      listing {
        askingPrice
        currency
        status
        soldPrice
      }
    }
  }
`;

// Item and storage counts. Reporting only — no plan enforces a quota yet.
export const GET_COLLECTION_USAGE = gql`
  query GetCollectionUsage($accountId: ID!) {
    collectionUsage(accountId: $accountId) {
      items
      published
      assets
      storageBytes
    }
  }
`;

export const GET_PUBLIC_SITE = gql`
  query GetPublicSite($accountId: ID!) {
    account(id: $accountId) {
      id
      name
      publicSlug
      publicEnabled
      publicTitle
      publicTagline
      publicAccessProtected
      publicSiteUrl
    }
  }
`;

// ── Collect: capture devices ────────────────────────────────────────────────

export const GET_CAPTURE_DEVICES = gql`
  query GetCaptureDevices($accountId: ID!) {
    captureDevices(accountId: $accountId) {
      id
      name
      kind
      connected
      lastUsedAt
      lastUsedIp
      tokenCount
      isActive
      createdAt
    }
  }
`;

export const GET_COLLECTION_TEMPLATES = gql`
  query GetCollectionTemplates {
    collectionTemplates {
      key
      label
      fieldCount
      dimensions
    }
  }
`;

// ── Collect: eBay ───────────────────────────────────────────────────────────
// No credential ever appears here. The refresh token lives server-side in the
// secret store; this is connection state plus listing preferences.

export const GET_EBAY_SETTINGS = gql`
  query GetEbaySettings($accountId: ID!) {
    ebaySettings(accountId: $accountId) {
      accountId
      connected
      ebayUsername
      environment
      environmentMismatch
      connectedAt
      refreshExpiresAt
      expiringSoon
      marketplaceId
      defaultCategoryId
      paymentPolicyId
      returnPolicyId
      fulfillmentPolicyId
      titlePattern
      defaultCondition
      defaultQuantity
    }
  }
`;

export const GET_EBAY_POLICIES = gql`
  query GetEbayPolicies($accountId: ID!) {
    ebayPolicies(accountId: $accountId) {
      payment {
        id
        name
      }
      return {
        id
        name
      }
      fulfillment {
        id
        name
      }
      syncedAt
      error
    }
  }
`;

// ── tinycms: publishing checks + style documents ────────────────────────────

export const GET_TINYCMS_SETTINGS = gql`
  query GetTinycmsSettings($accountId: ID!) {
    tinycmsSettings(accountId: $accountId) {
      copyEditRequired
      styleGuide
      voiceNotes
      styleGuideUpdatedAt
      voiceNotesUpdatedAt
      canManage
    }
  }
`;

// ── tinycms admin ───────────────────────────────────────────────────────────

const TINYCMS_ARTICLE_FIELDS = `
  id
  slug
  title
  workingHash
  state
  publicPriority
  isActive
  updatedAt
`;

export const GET_TINYCMS_ARTICLES = gql`
  query GetTinycmsArticles($accountId: ID!, $state: String) {
    tinycmsArticles(accountId: $accountId, state: $state) {
      ${TINYCMS_ARTICLE_FIELDS}
    }
  }
`;

export const GET_TINYCMS_ARTICLE = gql`
  query GetTinycmsArticle($accountId: ID!, $id: ID!) {
    tinycmsArticle(accountId: $accountId, id: $id) {
      ${TINYCMS_ARTICLE_FIELDS}
      content
      dataset
    }
  }
`;

export const GET_TINYCMS_DATASET = gql`
  query GetTinycmsDataset($accountId: ID!, $articleId: ID!, $includeDocument: Boolean) {
    tinycmsDataset(accountId: $accountId, articleId: $articleId, includeDocument: $includeDocument)
  }
`;

export const GET_TINYCMS_SITE = gql`
  query GetTinycmsSite($accountId: ID!) {
    tinycmsSite(accountId: $accountId)
  }
`;

// Pages (site structure built from blocks). Edited by the agent over MCP; the
// admin lists, checks, previews and publishes them.
/** Articles deleted in the last 30 days: [{ id, title, path, deletedAt, daysLeft }]. */
export const GET_TINYCMS_DELETED_ARTICLES = gql`
  query GetTinycmsDeletedArticles($accountId: ID!) {
    tinycmsDeletedArticles(accountId: $accountId)
  }
`;

/** Pages deleted in the last 30 days: [{ id, title, path, deletedAt, daysLeft }]. */
export const GET_TINYCMS_DELETED_PAGES = gql`
  query GetTinycmsDeletedPages($accountId: ID!) {
    tinycmsDeletedPages(accountId: $accountId)
  }
`;

/** Forms deleted in the last 30 days: [{ id, title, detail, responses, deletedAt, daysLeft }]. */
export const GET_TINYCMS_DELETED_FORMS = gql`
  query GetTinycmsDeletedForms($accountId: ID!) {
    tinycmsDeletedForms(accountId: $accountId)
  }
`;

export const GET_TINYCMS_PAGES = gql`
  query GetTinycmsPages($accountId: ID!, $state: String) {
    tinycmsPages(accountId: $accountId, state: $state)
  }
`;

export const GET_TINYCMS_PAGE = gql`
  query GetTinycmsPage($accountId: ID!, $id: ID!) {
    tinycmsPage(accountId: $accountId, id: $id)
  }
`;

/**
 * What changed between two site documents, in plain words:
 * { from, to, changes: [{ path, label, before, after }] }. from/to are version
 * numbers; `to` omitted is the working copy, `from` omitted is the live version
 * (no arguments: what publishing now would change); 0 is the preset defaults.
 */
export const TINYCMS_SITE_CHANGES = gql`
  query TinycmsSiteChanges($accountId: ID!, $from: Int, $to: Int) {
    tinycmsSiteChanges(accountId: $accountId, from: $from, to: $to)
  }
`;

export const GET_TINYCMS_APPROVALS = gql`
  query GetTinycmsApprovals(
    $accountId: ID!
    $kind: TinyCmsKind
    $subjectId: ID
    $limit: Int
    $offset: Int
  ) {
    tinycmsApprovals(
      accountId: $accountId
      kind: $kind
      subjectId: $subjectId
      limit: $limit
      offset: $offset
    ) {
      id
      kind
      subjectId
      action
      approvedBy
      approvedAt
      contentHash
      scheduledFor
      outcome
      refusalReason
      versionNumber
      findings
      overrideReason
      note
      subjectTitle
      subjectPath
      via
      recordedAt
    }
  }
`;

/**
 * Today in one read for [from, to): what waits (with its checks), what is
 * scheduled, what was approved, and the agent's edits, bucketed.
 */
export const GET_TINYCMS_TODAY = gql`
  query GetTinycmsToday($accountId: ID!, $from: String!, $to: String!) {
    tinycmsToday(accountId: $accountId, from: $from, to: $to)
  }
`;

export const GET_TINYCMS_DOMAINS = gql`
  query GetTinycmsDomains($accountId: ID!) {
    tinycmsDomains(accountId: $accountId)
  }
`;

export const GET_TINYCMS_INTEGRATIONS = gql`
  query GetTinycmsIntegrations($accountId: ID!) {
    tinycmsIntegrations(accountId: $accountId)
    tinycmsIntegrationVersions(accountId: $accountId, limit: 25)
  }
`;

/**
 * Licensed-feed keys (Site settings → Licensed feeds): { keys: [{ id, label,
 * keyPrefix, collections, tags, expiresAt, createdAt, lastUsedAt, revokedAt,
 * status, usage30d: { requests, items } }], canManage }. Never the key itself.
 */
export const GET_TINYCMS_FEED_KEYS = gql`
  query GetTinycmsFeedKeys($accountId: ID!) {
    tinycmsFeedKeys(accountId: $accountId)
  }
`;

/**
 * Site verification and ads.txt (Site settings → Verification & ads.txt): {
 * verifications: [{ service, method, value, label, address? | metaTag? }],
 * adsTxt, appAdsTxt, version, updatedAt, adsAllowed, adsServed, services,
 * canManage }, plus the saved versions for restore.
 */
export const GET_TINYCMS_ROOT_FILES = gql`
  query GetTinycmsRootFiles($accountId: ID!) {
    tinycmsRootFiles(accountId: $accountId)
    tinycmsRootFileVersions(accountId: $accountId, limit: 25)
  }
`;

// ── Forms (Content → Forms) ──────────────────────────────────────────────────

/** The site's forms: name, pages placing them, state, response counts. */
export const GET_TINYCMS_FORMS = gql`
  query GetTinycmsForms($accountId: ID!) {
    tinycmsForms(accountId: $accountId)
  }
`;

/** One form: working copy (content), workingHash, state with preflight. */
export const GET_TINYCMS_FORM = gql`
  query GetTinycmsForm($accountId: ID!, $id: ID!) {
    tinycmsForm(accountId: $accountId, id: $id)
  }
`;

/** What readers sent through a form, newest first. Members only. */
export const GET_TINYCMS_FORM_RESPONSES = gql`
  query GetTinycmsFormResponses($accountId: ID!, $formId: ID!, $limit: Int, $offset: Int) {
    tinycmsFormResponses(accountId: $accountId, formId: $formId, limit: $limit, offset: $offset)
  }
`;

/** Unread responses across every form (the Content nav count). */
export const GET_TINYCMS_UNREAD_RESPONSES = gql`
  query GetTinycmsUnreadResponses($accountId: ID!) {
    tinycmsUnreadResponses(accountId: $accountId)
  }
`;

/** { retentionMonths, consent, canManage } */
export const GET_TINYCMS_FORM_SETTINGS = gql`
  query GetTinycmsFormSettings($accountId: ID!) {
    tinycmsFormSettings(accountId: $accountId)
  }
`;
